/**
 * Typed errors thrown by `@perplexicon/api-client` (docs/APP_PLAN.md §5.2,
 * §12 M2b; TECH_STACK §23, §52).
 *
 * Every failure the API can produce carries the uniform envelope
 * `{ error: { code, message, details?, requestId? } }` (TECH_STACK §52).
 * `ApiError.code` is the stable, machine-readable field a caller must
 * branch on — never `message`, which is UI copy and may change without
 * notice. `ApiError.code` is typed as `ErrorCode`, imported from
 * `@perplexicon/shared`, the single source of truth also used by
 * `apps/api/src/errors/codes.ts` — so a caller's `switch` over known codes
 * is checked against the exact set the server can actually return.
 *
 * A response that fails before the server can even produce a structured
 * envelope (network failure, timeout, non-JSON body, malformed envelope)
 * is surfaced as `ApiNetworkError` instead, so callers can distinguish
 * "the server told us no" from "we could not reach/parse a response from
 * the server" without inspecting message text.
 */
import type { ErrorCode } from '@perplexicon/shared';

/** Discriminant every error this package throws carries, for `instanceof`-free narrowing if preferred. */
export type ApiClientErrorKind = 'api' | 'network';

/**
 * Thrown when the server responded with a structured `{ error: {...} }`
 * envelope. `code` is always one of the server's declared `ErrorCode`
 * values; an envelope carrying an unrecognized code is still surfaced
 * (the type is widened to `ErrorCode | 'UNKNOWN_ERROR'`) rather than
 * silently coerced, so a client never pretends to understand a code it
 * does not.
 */
export class ApiError extends Error {
  readonly kind = 'api' as const;
  readonly code: ErrorCode | 'UNKNOWN_ERROR';
  readonly status: number;
  readonly details?: unknown;
  readonly requestId?: string;

  constructor(
    code: ErrorCode | 'UNKNOWN_ERROR',
    message: string,
    status: number,
    details?: unknown,
    requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
    this.requestId = requestId;
  }
}

/**
 * Thrown when a request never produced a decodable API error envelope:
 * the network request itself failed (offline, DNS, CORS, aborted), the
 * server returned a non-JSON body, or a JSON body that does not match the
 * `{ error: { code, message } }` shape (e.g. an upstream proxy's own error
 * page). Callers should treat this as "try again" / "check your
 * connection" rather than attempt to key UI off a `code`.
 */
export class ApiNetworkError extends Error {
  readonly kind = 'network' as const;
  override readonly cause?: unknown;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'ApiNetworkError';
    this.cause = cause;
  }
}

/** True for any error this package throws (`ApiError` or `ApiNetworkError`). */
export function isApiClientError(err: unknown): err is ApiError | ApiNetworkError {
  return err instanceof ApiError || err instanceof ApiNetworkError;
}
